import { CheckIcon } from '@assets/icons/CheckIcon';
import { ClearIcon } from '@assets/icons/ClearIcon';
import { GeneralMuiModal } from '@components/shared/general-mui-modal';
import { TaMaps } from '@components/shared/Maps/TaMaps';
import { useGeolocated } from '@components/shared/Maps/useGeoLocated';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { useModalDimensions } from '@hooks/useModalDimensions';
import { useTruncate } from '@hooks/useTruncate';
import { useWindowSize } from '@hooks/useWindowSize';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import {
	AccordionProps,
	Box,
	Breadcrumbs,
	CardMedia,
	CircularProgress,
	IconButton,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary, {
	accordionSummaryClasses,
	AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import { styled } from '@mui/material/styles';
import { useFarmStore, useSingleFarm } from '@pages/MyFarm/store';
import FsLightbox from 'fslightbox-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { mapUnit } from '../MyFarmMainPage/MyFarmMainPage';

// Add type definition at the top of the file after imports
interface FarmFile {
	id: string;
	filename: string;
	s3_key: string;
}

// Styled Components
const Accordion = styled((props: AccordionProps) => (
	<MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
	border: 'none',
	'&::before': {
		display: 'none',
	},
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
	<MuiAccordionSummary
		expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
		{...props}
	/>
))(() => ({
	padding: 0,
	width: 'max-content',
	backgroundColor: 'transparent',
	flexDirection: 'row',
	justifyContent: 'flex-start',
	'& .MuiAccordion-heading': {
		borderBottom: '1px solid red !important',
		paddingBottom: '20px',
	},
	[`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
		transform: 'rotate(90deg)',
	},
	[`& .${accordionSummaryClasses.expandIconWrapper}`]: {
		margin: 0,
	},
	[`& .${accordionSummaryClasses.content}`]: {
		margin: 0,
	},
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
	padding: theme.spacing(2),
	border: 'none',
}));

export function CropDetails({ showBreadcrumbs = true }: { showBreadcrumbs?: boolean }) {
	// Hooks and State
	const { t } = useTranslation();
	const { id } = useParams();
	const { singleFarm } = useFarmStore();
	const size = useWindowSize();
	const { coords } = useGeolocated();
	const modalDimensions = useModalDimensions();
	const { navigateWithLanguage } = useLanguageNavigate();
	const { truncate } = useTruncate();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const isTablet = useMediaQuery(theme.breakpoints.down('xl'));

	// Local State
	const [mapOpen, setMapOpen] = useState(false);
	const [expanded, setExpanded] = useState<string | false>('requests');
	const [selectedImage, setSelectedImage] = useState<string | null>(null);
	const [lightboxOpen, setLightboxOpen] = useState(false);
	const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);

	// Data Fetching
	const { isLoading } = useSingleFarm(id || '');

	// Memoized Values
	const initialPosition = useMemo(
		() => ({
			lat: coords?.latitude || 0,
			lng: coords?.longitude || 0,
		}),
		[coords]
	);

	const tableData = useMemo(() => {
		return singleFarm?.history || [];
	}, [singleFarm]);

	const checkboxFields = useMemo(() => {
		if (singleFarm) {
			return Object.entries(singleFarm)
				.filter(([key, value]) => typeof value === 'boolean')
				.map(([key, value]) => ({
					label: key,
					name: key,
					checked: value,
				}));
		}
		return [];
	}, [singleFarm]);

	const variety = useMemo(() => {
		if (singleFarm) {
			return singleFarm?.crop_variety.join(', ');
		}
		return '';
	}, [singleFarm]);

	const markerPosition = useMemo(
		() => ({
			lng: Number(singleFarm?.crop_longitude ?? initialPosition.lng),
			lat: Number(singleFarm?.crop_latitude ?? initialPosition.lat),
		}),
		[singleFarm?.crop_longitude, singleFarm?.crop_latitude, initialPosition]
	);

	const imageFiles = useMemo(() => {
		return (singleFarm?.farm_files || []).filter((file: FarmFile) => {
			const extension = file.filename.split('.').pop()?.toLowerCase() || '';
			return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension);
		});
	}, [singleFarm?.farm_files]);

	const documentFiles = useMemo(() => {
		return (singleFarm?.farm_files || []).filter((file: FarmFile) => {
			const extension = file.filename.split('.').pop()?.toLowerCase() || '';
			return !['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension);
		});
	}, [singleFarm?.farm_files]);

	const imageElements = useMemo(() => {
		return imageFiles.map((file: FarmFile) => {
			const src = file.s3_key.startsWith('http')
				? file.s3_key
				: `${process.env.REACT_APP_CDN_URL || ''}${file.s3_key.startsWith('/') ? '' : '/'}${file.s3_key}`;

			return (
				<div
					key={file.id}
					style={{
						width: '100%',
						height: '100%',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<img
						src={src}
						alt={file.filename}
						style={{ maxHeight: '90vh', maxWidth: '90vw', objectFit: 'contain' }}
					/>
				</div>
			);
		});
	}, [imageFiles]);

	// Event Handlers
	const handleChange = useCallback(
		(panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
			setExpanded(newExpanded ? panel : false);
		},
		[]
	);

	const openMapsModal = useCallback(() => setMapOpen(true), []);
	const closeMapsModal = useCallback(() => setMapOpen(false), []);

	const handleCloseLightbox = useCallback(() => {
		setLightboxOpen(false);
	}, []);

	const handleImageClick = useCallback((index: number) => {
		setSelectedImageIndex(index);
		setLightboxOpen(true);
	}, []);

	// Loading state
	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" height="100vh">
				<CircularProgress style={{ color: '#038877' }} />
			</Box>
		);
	}

	return (
		<div>
			{showBreadcrumbs ?? (
				<Breadcrumbs aria-label="breadcrumb" className="!mb-[20px]">
					<a
						className="text-secondary font-roboto cursor-pointer text-base font-normal leading-[140%] hover:underline"
						onClick={() => navigateWithLanguage('/farmer/my-farm')}
					>
						{t('myFarm.myFarm')}
					</a>
					<a className="text-dark-blue font-roboto text-base font-normal leading-[140%]">
						{t('myFarm.myCrop')}
					</a>
				</Breadcrumbs>
			)}
			<div className="flex gap-[22px]">
				<div className="relative">
					<CardMedia
						component="img"
						image={singleFarm?.crop.image_url}
						alt="Blueberry"
						sx={{
							width: 74,
							height: 74,
							borderRadius: '12px',
							boxShadow: theme.boxShadows.primary,
						}}
					/>
					{/* <div className="absolute  bottom-[-10px] right-[-10px] flex items-center justify-center rounded-lg bg-custom-yellow p-1.5 text-white cursor-pointer h-6 w-6 text-xs">
						<EditOutlinedIcon className="!text-[14px]" />
					</div> */}
				</div>
				<div className="flex flex-col gap-[10px]">
					<h4 className="text-dark font-roboto text-xl font-bold leading-[140%]">
						{singleFarm?.crop.name}
					</h4>
					<div className="flex ">
						<h6 className="text-secondary font-roboto text-base font-normal leading-[140%]">
							{`${singleFarm?.crop_area} ${mapUnit(singleFarm?.crop_unit)}`}
						</h6>
						{variety && <div className="h-[20px] border-r border-[#E9ECEF] mx-[20px]"></div>}
						<h6 className="text-secondary font-roboto text-base font-normal leading-[140%]">
							{variety}
						</h6>
					</div>
				</div>
			</div>
			<div className="flex gap-[10px] items-center mt-[25px] border-b border-[#E9ECEF] pb-[20px] mb-[20px]">
				<FmdGoodOutlinedIcon color="primary" />
				<p className="text-secondary font-roboto text-sm font-normal leading-[140%]">
					{singleFarm?.crop_location_name || 'N/A'}
				</p>
				<div className="rounded-[12px] bg-light-grey">
					<IconButton
						onClick={openMapsModal}
						classes={{ root: 'rounded-[12px]' }}
						aria-label="delete"
					>
						<PublicOutlinedIcon />
					</IconButton>
				</div>
			</div>

			<div className="flex flex-col gap-[20px] pb-5 border-b border-[#E9ECEF] pb-[20px] mb-[20px]">
				<h6 className="text-dark font-roboto text-base font-bold leading-[140%]">
					{t('additional-information')}
				</h6>
				<div className="flex flex-col md:flex-row">
					<div className="w-full md:w-1/2">
						{checkboxFields.slice(0, 4).map((field) => (
							<div key={field.name}>
								<div className="flex mb-[15px] items-center gap-[10px]">
									{field.checked ? <CheckIcon /> : <ClearIcon />}
									<p className="text-secondary font-roboto text-base font-normal leading-[140%]">
										{field.label === 'irrigation_system' && field.checked ? (
											<span className="text-secondary font-roboto text-base font-normal leading-[140%] ">
												{singleFarm?.irrigationSystem.name}
											</span>
										) : (
											<span className="text-secondary font-roboto text-base font-normal leading-[140%] ">
												{t(`myFarm.${field.label}`)}
											</span>
										)}
									</p>
								</div>
							</div>
						))}
					</div>
					<div className="w-full md:w-1/2">
						{checkboxFields.slice(4).map((field) => (
							<div key={field.name} className="flex mb-[15px] items-center gap-[10px]">
								{field.checked ? <CheckIcon /> : <ClearIcon />}
								<p className="text-secondary font-roboto text-base font-normal leading-[140%]">
									{t(`myFarm.${field.label}`)}
								</p>
							</div>
						))}
						{documentFiles.length > 0 || imageFiles.length > 0 ? (
							<>
								<div className="flex mb-[15px] items-center gap-[10px]">
									<CheckIcon />
									<p className="text-secondary font-roboto text-base font-normal leading-[140%]">
										{t(`myFarm.soil_analysis`)}
									</p>
								</div>
								{documentFiles.length > 0 && (
									<div className="flex gap-[10px] pl-[20px] flex-col items-start">
										{documentFiles.map((file: FarmFile) => (
											<a
												key={file.id}
												href={file.s3_key}
												className="flex gap-[10px] items-center cursor-pointer"
											>
												<AttachFileIcon className="text-primary" />
												<p className="text-primary roboto-bold text-[16px] underline">
													{isMobile
														? truncate(file.filename, 25)
														: isTablet
															? truncate(file.filename, 30)
															: file.filename}
												</p>
											</a>
										))}
									</div>
								)}
							</>
						) : (
							<div className="flex mb-[15px] items-center gap-[10px]">
								<ClearIcon />
								<p className="text-secondary font-roboto text-base font-normal leading-[140%]">
									{t(`myFarm.soil_analysis`)}
								</p>
							</div>
						)}
					</div>
				</div>
			</div>

			{imageFiles.length > 0 && (
				<div className="flex flex-col gap-[20px] pb-5 border-b border-[#E9ECEF] pb-[20px] mb-[20px]">
					<h6 className="text-dark font-roboto text-base font-bold leading-[140%]">
						{t('attachments')}
					</h6>
					<div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
						{imageFiles.map((file: FarmFile, index: number) => (
							<div
								key={file.id}
								className="relative group cursor-pointer w-full aspect-square"
								onClick={() => handleImageClick(index)}
							>
								<div className="relative w-full h-full rounded-[12px] overflow-hidden shadow-sm border border-gray-100">
									<img
										src={
											file.s3_key.startsWith('http')
												? file.s3_key
												: `${process.env.REACT_APP_CDN_URL || ''}${file.s3_key.startsWith('/') ? '' : '/'}${file.s3_key}`
										}
										alt={file.filename}
										className="w-full h-full object-cover"
									/>
									<div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
										<ZoomInIcon className="text-white opacity-0 group-hover:opacity-100 transition-all duration-300" />
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			)}

			<GeneralMuiModal
				maxWidth={'xl'}
				title={t('myFarm.farmLocation')}
				styles={{
					paperStyle: { width: modalDimensions.width, height: modalDimensions.height, padding: 0 },
					contentStyle: { padding: 0 },
				}}
				open={mapOpen}
				onClose={closeMapsModal}
				content={
					<TaMaps
						disabled={true}
						marker={markerPosition}
						currentAddress={singleFarm?.crop_location_name}
					/>
				}
			/>

			<FsLightbox
				toggler={lightboxOpen}
				sources={imageElements}
				sourceIndex={selectedImageIndex}
				onClose={handleCloseLightbox}
				type="node"
			/>
		</div>
	);
}
